import {
  ORDER_TYPE_ENUM,
  PAYMENT_METHOD_ENUM,
  platformKey,
} from "@/constants/enums";
import type { OrderType, PaymentMethod } from "@/constants/enums";
import type { CartLine, StoreSettings } from "@/types";

/**
 * The single source of truth for order money.
 *
 * The basket screen and the backend previously each computed the total from
 * their own copy of the formula. They happened to agree, but nothing enforced
 * it — and a customer seeing one total while being charged another is the worst
 * class of bug this app could ship. Both now call this function.
 *
 * Assumption worth flagging: **GST is applied to the subtotal only**, not to
 * the delivery fee or packaging charge. The original bundle did not make the
 * treatment of fees explicit, and taxing them would silently inflate every
 * delivery order. If the tenant's accountant says otherwise, change it here and
 * both the estimate and the charge move together.
 */

export interface PricingInput {
  items: CartLine[];
  orderType: OrderType;
  paymentMethod?: PaymentMethod | null;
  settings: StoreSettings;
  /** Charge for the selected delivery area, before any free-delivery threshold. */
  deliveryCost?: number;
  /**
   * Straight-line distance to the delivery address. Only used when the tenant
   * has opted into radius-based delivery pricing; absent for flat per-area
   * pricing, which is what the delivery-areas list provides today.
   */
  distanceMeters?: number;
  /**
   * `cart.tipAmount` — the customer's tip, in major currency units.
   *
   * It is the only money input here the customer chooses outright, and it is
   * added AFTER tax: the original's totals list puts `tip_amount` at row 16,
   * below every charge and immediately above the grand total, and nothing
   * levies tax on a tip. `:4001` adds it in the same position, so the two
   * grand totals agree.
   */
  tipAmount?: number;
  /** Defaults to now. Injectable so surcharge tests are not clock-dependent. */
  now?: Date;
}

export interface OrderTotals {
  subtotal: number;
  delivery: number;
  packaging: number;
  /** Dine-in service charge, flat or a percentage of the subtotal. */
  serviceFee: number;
  /** Holiday surcharge, when the tenant has opted in. */
  surcharge: number;
  /** Basis points, as stored: 500 means 5%. */
  gstBasisPoints: number;
  gstRate: number;
  tax: number;
  /** The tip as it will be charged: negatives and non-numbers collapse to 0. */
  tip: number;
  total: number;
  /** Set when the basket is below the configured minimum for this order type. */
  belowMinimum?: { minimum: number; shortfall: number };
}

/**
 * `calculateItemModifiersPrice` (module `6907`'s `f`), recovered verbatim:
 *
 * ```js
 * i.is_free || ((t += i.price * i.quantity), r(i.modifiers ?? []));
 * ```
 *
 * Two rules, both of which this port previously dropped: a FREE option (one of
 * the group's `num_free_options` cheapest picks) contributes nothing, and every
 * other option is multiplied by the quantity the customer chose. `quantity`
 * defaults to 1 so a basket persisted before the field existed still prices.
 *
 * Nested option-level modifiers are not modelled by this tree — the options
 * drawer builds a flat list — so the original's recursion has no counterpart
 * here.
 */
export const modifiersPrice = (item: CartLine): number =>
  (item.modifiers ?? []).reduce(
    (sum, modifier) =>
      modifier.is_free
        ? sum
        : sum + (modifier.price ?? 0) * (modifier.quantity ?? 1),
    0,
  );

/**
 * What one of this basket row costs *as configured* — the base item plus every
 * option the customer chose. This is the number to show against a basket line;
 * `item.price` alone omits the options and will not sum to the subtotal.
 *
 * This is the original's `(item.price + modifiersPrice)`, and `item.price` is
 * the BARE item price for exactly that reason. The options drawer used to store
 * `item.price + modifiersPrice` on the line AND hand the same options over
 * separately, so this function added the surcharge a second time: a Rs 450 item
 * with a Rs 20 option was shown and posted at Rs 490 a unit.
 */
export const unitPrice = (item: CartLine): number =>
  item.price + modifiersPrice(item);

export const lineTotal = (item: CartLine): number =>
  unitPrice(item) * item.quantity;

/**
 * The same unit at its UNDISCOUNTED price. `listPrice` is bare, exactly like
 * `price`, so the options have to be added back before it can be compared with
 * `unitPrice` — otherwise a discounted item with any option chosen compares
 * `450` against `470` and the "you save" strike-through silently disappears.
 */
export const listUnitPrice = (item: CartLine): number | undefined =>
  item.listPrice == null ? undefined : item.listPrice + modifiersPrice(item);

export const listLineTotal = (item: CartLine): number | undefined => {
  const unit = listUnitPrice(item);
  return unit == null ? undefined : unit * item.quantity;
};

export const calculateSubtotal = (items: CartLine[] = []): number =>
  items.reduce((sum, item) => sum + lineTotal(item), 0);

/** A threshold of 0 means "no threshold", not "always free". */
const meetsThreshold = (subtotal: number, threshold?: number): boolean =>
  Boolean(threshold && threshold > 0 && subtotal >= threshold);

/**
 * What the delivery leg costs before any free-delivery threshold.
 *
 * Two models. Flat per-area is what the delivery-areas list provides and what
 * this tenant uses. Radius-based prices by distance, and only applies when the
 * tenant has opted in *and* a distance is available — the areas list carries
 * none, so this branch stays dormant until a backend supplies one from a
 * geocoded address. It is implemented and tested rather than omitted, because
 * a tenant who switches the flag on should not silently get free delivery.
 */
export const resolveDeliveryCost = ({
  settings,
  flatCost = 0,
  distanceMeters,
}: {
  settings: StoreSettings;
  flatCost?: number;
  distanceMeters?: number;
}): number => {
  if (!settings.opt_in_for_radius_based_delivery || distanceMeters == null) {
    return flatCost;
  }

  // Distance below the free radius is not charged for.
  const freeUpTo = settings.delivery_min_cost_distance_mtrs ?? 0;
  const chargeable = Math.max(0, distanceMeters - freeUpTo);
  const perMeter = settings.delivery_cost_per_meter ?? 0;
  const multiplier = settings.static_delivery_cost_multiplier || 1;

  // `delivery_min_cost` is a floor, not a base — a short hop still pays it.
  const computed = chargeable * perMeter * multiplier;
  return Math.max(settings.delivery_min_cost ?? 0, computed);
};

/**
 * The dine-in service charge. `is_service_fee_percentage` decides whether the
 * stored number is a percentage of the subtotal or a flat amount, and the fee
 * only applies once the basket clears `min_service_fee_order_value`.
 */
export const resolveServiceFee = (
  subtotal: number,
  orderType: OrderType,
  settings: StoreSettings,
): number => {
  if (orderType !== ORDER_TYPE_ENUM.DINE_IN) return 0;

  const fee = settings.dinein_service_fee ?? 0;
  if (fee <= 0) return 0;
  if (subtotal < (settings.min_service_fee_order_value ?? 0)) return 0;

  return settings.is_service_fee_percentage ? subtotal * (fee / 100) : fee;
};

/**
 * Holiday surcharge, in basis points like GST. Gated on the opt-in flag so a
 * leftover value cannot start charging customers on its own.
 */
export const resolveSurcharge = (
  subtotal: number,
  settings: StoreSettings,
): number => {
  if (!settings.web_opt_in_for_holiday_surcharge) return 0;
  return subtotal * ((settings.web_holiday_surcharge ?? 0) / 10000);
};

/**
 * The tenant's OWN default payment method, from `default_gst_type`.
 *
 * The original seeds a new cart's `gst` from it —
 * `x.default_gst_type === GST_TYPE_ENUM.CASH ? x.cash_gst : x.card_gst`
 * (module `21764`) — and `:4001` applies the same rule when an order arrives
 * with no `payment_method` (`mongo-api/src/routes/web.js`). So it is the right
 * answer for "the customer has not chosen yet", on both sides of the wire.
 */
export const defaultPaymentMethod = (settings: StoreSettings): PaymentMethod =>
  String(settings.default_gst_type ?? "").toUpperCase() ===
  PAYMENT_METHOD_ENUM.CASH
    ? PAYMENT_METHOD_ENUM.CASH
    : PAYMENT_METHOD_ENUM.CARD;

/**
 * GST rate in basis points for this order, or 0 when the tenant has exempted
 * the platform. `exempt_gst_for_platforms` holds keys like "web_delivery".
 *
 * A `null` payment method means "not chosen yet" and resolves to the tenant's
 * `default_gst_type`, NOT to card. Falling through to `card_gst` was the same
 * silent assumption `:4001` used to make in the other direction, and on a
 * tenant whose default is cash the estimate and the charge disagreed by the
 * whole difference between the two rates.
 */
export const resolveGstBasisPoints = (
  orderType: OrderType,
  paymentMethod: PaymentMethod | null | undefined,
  settings: StoreSettings,
): number => {
  // The tenant's list is empty, so there is no sample of the back office's
  // casing to check against. Compare case-insensitively rather than guess:
  // getting this wrong means charging tax the restaurant has exempted.
  const key = platformKey(orderType).toLowerCase();
  const exempt = (settings.exempt_gst_for_platforms ?? []).map((value) =>
    String(value).toLowerCase(),
  );
  if (exempt.includes(key)) return 0;

  const effective = paymentMethod ?? defaultPaymentMethod(settings);
  return effective === PAYMENT_METHOD_ENUM.CASH
    ? (settings.cash_gst ?? 0)
    : (settings.card_gst ?? 0);
};

export const calculateOrderTotals = ({
  items,
  orderType,
  paymentMethod,
  settings,
  deliveryCost = 0,
  distanceMeters,
  tipAmount = 0,
}: PricingInput): OrderTotals => {
  const subtotal = calculateSubtotal(items);
  const isDelivery = orderType === ORDER_TYPE_ENUM.DELIVERY;

  const delivery =
    isDelivery && !meetsThreshold(subtotal, settings.delivery_free_over)
      ? resolveDeliveryCost({
          settings,
          flatCost: deliveryCost,
          distanceMeters,
        })
      : 0;

  const packagingApplies =
    subtotal >= (settings.min_packaging_charges_order_value ?? 0) &&
    !meetsThreshold(subtotal, settings.packaging_charges_free_over);
  const packaging = packagingApplies ? (settings.packaging_charges ?? 0) : 0;

  const serviceFee = resolveServiceFee(subtotal, orderType, settings);
  const surcharge = resolveSurcharge(subtotal, settings);

  const gstBasisPoints = resolveGstBasisPoints(
    orderType,
    paymentMethod,
    settings,
  );
  const gstRate = gstBasisPoints / 10000;
  const tax = subtotal * gstRate;

  const minimum = isDelivery
    ? (settings.min_delivery_order_value ?? 0)
    : (settings.web_min_takeaway_order_value ?? 0);

  // Same floor `:4001` applies, and the same reason: a negative here would be
  // a discount the customer awarded themselves. The minimum-order test above
  // stays on the subtotal, so a tip cannot buy past a minimum either.
  const tip = Number.isFinite(tipAmount) && tipAmount > 0 ? tipAmount : 0;

  return {
    subtotal,
    delivery,
    packaging,
    serviceFee,
    surcharge,
    gstBasisPoints,
    gstRate,
    tax,
    tip,
    total: subtotal + delivery + packaging + serviceFee + surcharge + tax + tip,
    ...(minimum > 0 && subtotal < minimum
      ? { belowMinimum: { minimum, shortfall: minimum - subtotal } }
      : {}),
  };
};

/** "5%" or "12.5%" — avoids a trailing ".0" on whole percentages. */
export const formatGstLabel = (gstRate: number): string => {
  const percent = gstRate * 100;
  return `${percent.toFixed(percent % 1 ? 1 : 0)}%`;
};
